import { isValidPlacement } from "./validator";
import { hasUniqueSolution } from "./solver";
// ---------------------------------------------------------------------------
// Seeded PRNG (mulberry32) — lets us reproduce puzzles from a seed/ID
// ---------------------------------------------------------------------------
function mulberry32(seed) {
    return function () {
        seed |= 0;
        seed = (seed + 0x6d2b79f5) | 0;
        let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
function shuffle(arr, rng) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        const tmp = a[i];
        a[i] = a[j];
        a[j] = tmp;
    }
    return a;
}
// ---------------------------------------------------------------------------
// Step 1: Generate a fully solved grid
// ---------------------------------------------------------------------------
function generateSolution(rng) {
    const grid = new Array(81).fill(0);
    function fill(pos) {
        if (pos === 81)
            return true;
        const row = Math.floor(pos / 9);
        const col = pos % 9;
        const candidates = shuffle([1, 2, 3, 4, 5, 6, 7, 8, 9], rng);
        for (const v of candidates) {
            if (isValidPlacement(grid, row, col, v)) {
                grid[pos] = v;
                if (fill(pos + 1))
                    return true;
                grid[pos] = 0;
            }
        }
        return false;
    }
    fill(0);
    return grid;
}
const DIFFICULTY_PARAMS = {
    easy: { givens: 36, maxAttempts: 40 },
    medium: { givens: 27, maxAttempts: 60 },
    hard: { givens: 22, maxAttempts: 80 },
};
// ---------------------------------------------------------------------------
// Step 3: Dig holes in the solution
// ---------------------------------------------------------------------------
function digHoles(solution, params, rng) {
    const puzzle = [...solution];
    const cells = shuffle(Array.from({ length: 81 }, (_, i) => i), rng);
    let filled = 81;
    let attempts = 0;
    for (const cell of cells) {
        if (filled <= params.givens)
            break;
        if (attempts >= params.maxAttempts)
            break;
        const backup = puzzle[cell] ?? 0;
        puzzle[cell] = 0;
        attempts++;
        if (hasUniqueSolution(puzzle)) {
            filled--;
        }
        else {
            puzzle[cell] = backup; // Restore — removing this cell breaks uniqueness
        }
    }
    return puzzle;
}
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Derives a stable numeric seed from an arbitrary string.
 */
function hashSeed(s) {
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}
/**
 * Generates a unique puzzle ID from the puzzle content.
 */
function puzzleId(puzzle) {
    // Base-10 representation, non-zero givens replaced with their value
    return puzzle.map((v) => (v === 0 ? "." : String(v))).join("");
}
/**
 * Generates a new Sudoku puzzle.
 *
 * @param difficulty  "easy" | "medium" | "hard"
 * @param seed        Optional seed string for reproducibility (e.g. a date for
 *                    daily puzzles). Omit for a random puzzle.
 */
export function generatePuzzle(difficulty, seed) {
    const numSeed = seed
        ? hashSeed(seed)
        : (Math.random() * 0xffffffff) >>> 0;
    const rng = mulberry32(numSeed);
    const params = DIFFICULTY_PARAMS[difficulty];
    const solution = generateSolution(rng);
    const puzzle = digHoles(solution, params, rng);
    return {
        puzzle,
        solution,
        difficulty,
        id: puzzleId(puzzle),
    };
}
/**
 * Generates a daily puzzle deterministically from today's date string.
 * All users on the same day get the same puzzle.
 *
 * @param dateStr  ISO date string, e.g. "2025-06-01". Defaults to today.
 */
export function generateDailyPuzzle(difficulty, dateStr) {
    const date = dateStr ?? new Date().toISOString().slice(0, 10);
    return generatePuzzle(difficulty, `daily-${difficulty}-${date}`);
}
